import { toast } from 'sonner';
import type { PostgrestError } from '@supabase/supabase-js';

// Error types for classification
export type ErrorType = 'auth' | 'network' | 'validation' | 'permission' | 'rate_limit' | 'server' | 'generic';

export interface ErrorContext {
  errorId?: string;
  userId?: string;
  action?: string;
  component?: string;
  timestamp?: string;
  userAgent?: string;
  url?: string;
  additionalData?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ClassifiedError {
  type: ErrorType;
  message: string;
  userMessage: string;
  retryable: boolean;
  context?: ErrorContext;
}

// Error classification functions
export function isAuthError(error: Error | PostgrestError): boolean {
  const message = error.message.toLowerCase();
  const authKeywords = [
    'unauthorized', 'auth', 'token', 'login', 'session',
    'permission denied', 'access denied', 'forbidden',
    'jwt', 'authentication', 'invalid_grant'
  ];
  
  return authKeywords.some(keyword => message.includes(keyword)) ||
    ('code' in error && ['401', 'PGRST301', 'PGRST302'].includes(String(error.code)));
}

export function isNetworkError(error: Error): boolean {
  const message = error.message.toLowerCase();
  const networkKeywords = [
    'network', 'fetch', 'connection', 'timeout', 'offline',
    'cors', 'net::', 'failed to fetch', 'load failed'
  ];
  
  return networkKeywords.some(keyword => message.includes(keyword)) ||
    error.name === 'NetworkError' ||
    error.name === 'TypeError' && message.includes('fetch');
}

export function isValidationError(error: Error | PostgrestError): boolean {
  const message = error.message.toLowerCase();
  const validationKeywords = [
    'validation', 'required', 'invalid', 'format',
    'constraint', 'unique', 'foreign key', 'check constraint'
  ];
  
  return validationKeywords.some(keyword => message.includes(keyword)) ||
    ('code' in error && ['23505', '23503', '23514', 'PGRST204'].includes(String(error.code)));
}

export function isPermissionError(error: Error | PostgrestError): boolean {
  const message = error.message.toLowerCase();
  return message.includes('permission') ||
    message.includes('access denied') ||
    message.includes('insufficient privileges') ||
    ('code' in error && ['42501', 'PGRST301'].includes(String(error.code)));
}

export function isRateLimitError(error: Error): boolean {
  const message = error.message.toLowerCase();
  return message.includes('rate limit') ||
    message.includes('too many requests') ||
    error.message.includes('429');
}

export function isServerError(error: Error | PostgrestError): boolean {
  const message = error.message.toLowerCase();
  return message.includes('internal server error') ||
    message.includes('service unavailable') ||
    message.includes('bad gateway') ||
    ('code' in error && ['500', '502', '503', '504'].includes(String(error.code)));
}

// Parse Supabase-specific errors
export function parseSupabaseError(error: PostgrestError): ClassifiedError {
  const { code, message, details, hint } = error;
  
  // Map common Supabase error codes
  const codeMapping: Record<string, { type: ErrorType; userMessage: string; retryable: boolean }> = {
    'PGRST301': { 
      type: 'permission', 
      userMessage: 'You don\'t have permission to perform this action.',
      retryable: false 
    },
    'PGRST302': { 
      type: 'auth', 
      userMessage: 'Please log in to continue.',
      retryable: false 
    },
    'PGRST204': { 
      type: 'validation', 
      userMessage: 'The data you entered is invalid. Please check and try again.',
      retryable: false 
    },
    '23505': { 
      type: 'validation', 
      userMessage: 'This record already exists. Please use a different value.',
      retryable: false 
    },
    '23503': { 
      type: 'validation', 
      userMessage: 'This action would violate data constraints.',
      retryable: false 
    },
    '42501': { 
      type: 'permission', 
      userMessage: 'You don\'t have sufficient privileges for this operation.',
      retryable: false 
    }
  };

  const mapping = codeMapping[code] || {
    type: 'server' as ErrorType,
    userMessage: 'A server error occurred. Please try again.',
    retryable: true
  };

  return {
    type: mapping.type,
    message: `${message}${details ? ` - ${details}` : ''}${hint ? ` Hint: ${hint}` : ''}`,
    userMessage: mapping.userMessage,
    retryable: mapping.retryable
  };
}

// Classify any error
export function classifyError(error: Error | PostgrestError, context?: ErrorContext): ClassifiedError {
  // Handle Supabase errors
  if ('code' in error) {
    return parseSupabaseError(error as PostgrestError);
  }

  // Classify other errors
  let type: ErrorType = 'generic';
  let userMessage = 'An unexpected error occurred. Please try again.';
  let retryable = true;

  if (isAuthError(error)) {
    type = 'auth';
    userMessage = 'Your session has expired. Please log in again.';
    retryable = false;
  } else if (isNetworkError(error)) {
    type = 'network';
    userMessage = 'Connection problem. Please check your internet and try again.';
    retryable = true;
  } else if (isValidationError(error)) {
    type = 'validation';
    userMessage = 'Please check your input and try again.';
    retryable = false;
  } else if (isPermissionError(error)) {
    type = 'permission';
    userMessage = 'You don\'t have permission to perform this action.';
    retryable = false;
  } else if (isRateLimitError(error)) {
    type = 'rate_limit';
    userMessage = 'Too many requests. Please wait a moment and try again.';
    retryable = true;
  } else if (isServerError(error)) {
    type = 'server';
    userMessage = 'Server error. Our team has been notified.';
    retryable = true;
  }

  return {
    type,
    message: error.message,
    userMessage,
    retryable,
    context
  };
}

// Format error messages for user display
export function formatErrorMessage(error: Error | PostgrestError, action?: string): string {
  const classified = classifyError(error);
  
  if (action) {
    return `Failed to ${action}: ${classified.userMessage}`;
  }
  
  return classified.userMessage;
}

// Error logging with context
export function logError(error: Error | PostgrestError, context?: ErrorContext): void {
  const classified = classifyError(error, context);
  
  const logData = {
    error: {
      name: error.name,
      message: error.message,
      stack: error.stack,
      type: classified.type,
      retryable: classified.retryable
    },
    context: {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...context
    }
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    // Development logging removed for production build
  }

  // In production, you might want to send to an error reporting service
  // Example: Sentry, LogRocket, DataDog, etc.
  if (process.env.NODE_ENV === 'production') {
    // TODO: Implement production error reporting
    // Sentry.captureException(error, { extra: logData });
    
    // Production error reporting would go here
    // Example: Send to error monitoring service
  }
}

// Toast notification helpers for errors
export function showErrorToast(
  error: Error | PostgrestError,
  action?: string,
  options?: {
    duration?: number;
    action?: {
      label: string;
      onClick: () => void;
    };
  }
): void {
  const classified = classifyError(error);
  const message = formatErrorMessage(error, action);

  const toastOptions = {
    duration: options?.duration || (classified.type === 'validation' ? 5000 : 4000),
    action: options?.action
  };

  // Use different toast types based on error classification
  switch (classified.type) {
    case 'validation':
      toast.error(message, {
        ...toastOptions,
        description: 'Please correct the highlighted fields and try again.'
      });
      break;
    
    case 'network':
      toast.error(message, {
        ...toastOptions,
        description: 'Check your internet connection.',
        action: {
          label: 'Retry',
          onClick: () => window.location.reload()
        }
      });
      break;
    
    case 'auth':
      toast.error(message, {
        ...toastOptions,
        description: 'You will be redirected to the login page.',
        action: {
          label: 'Login',
          onClick: () => window.location.href = '/login'
        }
      });
      break;
    
    default:
      toast.error(message, toastOptions);
  }
}

// Success toast helper for consistency
export function showSuccessToast(message: string, description?: string): void {
  toast.success(message, {
    description,
    duration: 3000
  });
}

// Warning toast helper
export function showWarningToast(message: string, description?: string): void {
  toast.warning(message, {
    description,
    duration: 4000
  });
}

// Info toast helper
export function showInfoToast(message: string, description?: string): void {
  toast.info(message, {
    description,
    duration: 3000
  });
}